import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { hasAnyRole, type Role } from '@/lib/constants/roles';
import { canAny } from '@/lib/auth/permissions';
import { isAdmin } from '@/lib/auth/admin-access';
import type { PermissionKey } from '@/lib/constants/permissions';
import { AccessDeniedState } from './AccessDeniedState';
import { WorkEmailRequiredState } from './WorkEmailRequiredState';
import { journeySignInPath } from '@/lib/auth/post-auth-landing';

interface ProtectedRouteProps {
  /** When set, the user must hold one of these roles; otherwise access is denied. */
  allowedRoles?: Role[];
  /**
   * When set, the user must hold this permission key (AUTHZ-1) - or, if an
   * array, ANY one of them (e.g. a route reachable by either the broad
   * `team.manage` capability or the narrower `team.invite` one) - otherwise
   * redirected to the dashboard. Independent of `allowedRoles` - a route can
   * declare a persona-level role gate ("must be in this persona at all") and a
   * capability-level permission gate ("must hold this specific permission")
   * together; both must pass.
   */
  requiredPermission?: PermissionKey | PermissionKey[];
  /**
   * When set, blocks a personal/disposable account email (MYN-46 Create
   * Startup gate) with a dedicated `WorkEmailRequiredState` screen instead of
   * the generic `AccessDeniedState` copy, offering a way to the account edit
   * page to fix the email on file. The Backend enforces this independently
   * (StartupsService) - this is a UX shortcut, not the source of truth.
   */
  requiresWorkEmail?: boolean;
  /**
   * Admin console gate (MYN-144): any admin role, read live from
   * `adminPermissions`, not the Super Admin role - a Team Admin is an admin too.
   */
  requiresAdmin?: boolean;
}

/**
 * Gates authenticated routes. Waits while the session hydrates (no login-screen
 * flash), redirects unauthenticated users to login, and - when `allowedRoles`/
 * `requiredPermission` are set - renders `AccessDeniedState` (US-039 AC3) for a
 * user who fails either check, in place of the route content, rather than
 * silently redirecting. Both gates are independent and AND'd: a route can
 * declare a persona-level role gate ("must be in this persona at all") and a
 * capability-level permission gate ("must hold this specific permission")
 * together.
 */
export function ProtectedRoute({
  allowedRoles,
  requiredPermission,
  requiresWorkEmail,
  requiresAdmin,
}: ProtectedRouteProps = {}) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) return null;
  if (!isAuthenticated) {
    // A session that lapsed mid-journey keeps the Issue Report intent (FR-219).
    return <Navigate to={journeySignInPath()} replace />;
  }
  if (allowedRoles?.length && !hasAnyRole(user?.roles ?? [], allowedRoles)) {
    return <AccessDeniedState />;
  }
  if (requiresAdmin && !isAdmin(user)) {
    return <AccessDeniedState />;
  }
  if (requiredPermission && !canAny(user, requiredPermission)) {
    return <AccessDeniedState />;
  }
  if (requiresWorkEmail && user?.isWorkEmail === false) {
    return <WorkEmailRequiredState />;
  }
  return <Outlet />;
}
